import { motion } from 'framer-motion';
import { AlertTriangle, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';
import * as React from 'react';
import { userMessage } from '@/lib/api/errors';
import { cn } from '@/lib/utils';
import { Button } from './button';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4', className)} />;
}

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  id,
  label,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  id?: string;
  label?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-input',
      )}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 600, damping: 32 }}
        className={cn('block size-5 rounded-full bg-white shadow-md', checked ? 'ml-[22px]' : 'ml-0.5')}
      />
    </button>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex flex-col items-center justify-center gap-3 px-6 py-14 text-center', className)}
    >
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl bg-brand-gradient opacity-20 blur-xl" />
        <div className="relative flex size-14 items-center justify-center rounded-2xl border bg-card text-primary-ink shadow-soft [&_svg]:size-6">
          {icon}
        </div>
      </div>
      <div className="space-y-1">
        <p className="font-semibold">{title}</p>
        {description && <p className="mx-auto max-w-sm text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </motion.div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
        <AlertTriangle className="size-5" />
      </div>
      <p className="text-sm text-muted-foreground">{userMessage(error, "We couldn't load this. Please try again.")}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCw /> Try again
        </Button>
      )}
    </div>
  );
}

/** The API has no total count: show "Next" when a full page came back. */
export function Pager({
  offset,
  limit,
  count,
  onChange,
  loading,
}: {
  offset: number;
  limit: number;
  count: number;
  onChange: (offset: number) => void;
  loading?: boolean;
}) {
  if (offset === 0 && count < limit) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground">
      <span>
        {count === 0 ? 'No more results' : `Showing ${offset + 1}–${offset + count}`}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={offset === 0 || loading} onClick={() => onChange(Math.max(0, offset - limit))}>
          <ChevronLeft /> Previous
        </Button>
        <Button variant="outline" size="sm" disabled={count < limit || loading} onClick={() => onChange(offset + limit)}>
          Next <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

/** Segmented filter control with an animated active pill. */
export function Tabs<T extends string>({
  value,
  onChange,
  options,
  layoutId,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  layoutId: string;
  className?: string;
}) {
  return (
    <div className={cn('inline-flex max-w-full gap-1 overflow-x-auto rounded-xl bg-secondary/70 p-1', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'relative whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
            value === o.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {value === o.value && (
            <motion.span
              layoutId={layoutId}
              transition={{ type: 'spring', stiffness: 500, damping: 38 }}
              className="absolute inset-0 rounded-lg bg-card shadow-sm ring-1 ring-border"
            />
          )}
          <span className="relative">{o.label}</span>
        </button>
      ))}
    </div>
  );
}
